import { useState } from 'react'
import { FileAction, useFilesContext } from '../files-context'
import { CustomerState } from 'lib/api/ticket-files/ticket-files'
import { useMutation } from '@tanstack/react-query'
import { request } from 'lib/api/fetch-api'
import { toast } from 'lib/components/toast/toast'
import Modal from 'lib/components/modals/modal'
import { InfoIcon } from 'lucide-react'
import CurrentAsset from './current-asset-screen'
import Checkbox from 'lib/components/checkbox/checkbox'
import Button from 'components/core/button'
import Link from 'lib/components/link/link'
import { AUTO_SHOW_FILE_EXPORT_MODAL_KEY } from 'components/pages/request/media/state-button'

const ExportScreen = () => {
  const { files, setMode, onFinish } = useFilesContext()

  const [autoShow, setAutoShow] = useState(localStorage.getItem(AUTO_SHOW_FILE_EXPORT_MODAL_KEY) !== 'true')

  const approved = files.every((file) => file?.customerState === CustomerState.Approved)

  const { mutate: exportToStockPress } = useMutation({
    mutationFn: () => request({ endpoint: 'exportAssets', body: { ticket_file_ids: files.map((file) => file.id) } }),
    onSuccess: () => {
      setMode(null)
      onFinish?.({ action: FileAction.UPDATE, ticketFiles: files })
      toast.success(
        'Your file has been exported to Stockpress. It may take a few minutes to appear in your Stockpress account.',
      )
    },
    onError: (err) => {
      toast.error('There was an error exporting to Stockpress, please try again')
      console.error(err)
    },
  })

  return (
    <>
      <Modal.Header className="tw-font-bold">Export</Modal.Header>
      <Modal.Body
        className="tw-mt-4 tw-flex tw-flex-col tw-gap-4 tw-text-neutral-800"
        setOpen={() => setMode(null)}
        closeButton
      >
        {!approved && (
          <div className="tw-flex tw-flex-row tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-bg-skyblue-50 tw-p-4 tw-text-sky-700">
            <InfoIcon className="tw-mt-1 tw-h-4 tw-w-4 tw-flex-shrink-0" />
            Only assets marked as approved in the Design Pickle platform are eligible to be sent to Stockpress. To
            export directly to Stockpress, mark your asset as approved.
          </div>
        )}
        <CurrentAsset />
        <div className="tw-flex tw-flex-row tw-gap-2 tw-rounded tw-p-3">
          <div className="tw-rounded-lg tw-bg-neutral-100 tw-p-2">
            <img src={`/images/vendors/stockpress.png`} width={50} height={50} />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <div className="tw-text-md tw-font-semibold">Stockpress</div>
            <div>Simple Digital Asset Management for teams of all sizes</div>
            <Link target="_blank" rel="noopener noreferrer" href="https://stockpress.co/" externalIcon>
              See how our partners at Stockpress help manage assets
            </Link>
          </div>
        </div>

        <Checkbox
          label="Automatically show this modal when a file is approved"
          onClick={(value) => {
            setAutoShow(value.target.checked)
            localStorage.setItem(AUTO_SHOW_FILE_EXPORT_MODAL_KEY, value.target.checked ? 'false' : 'true')
          }}
          isChecked={autoShow}
          disabled={!approved}
        />

        <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
          <div className="tw-text-sm tw-font-semibold">1 asset selected</div>
          <Button color="purple" onClick={() => exportToStockPress()} disabled={!approved}>
            Export to Stockpress
          </Button>
        </div>
      </Modal.Body>
    </>
  )
}

export default ExportScreen
